import "./Filter.css";
import React, { useState } from "react";
import { Filter as FilterIcon, Moon, Sun, Info } from "lucide-react";

import Config from "../../config.json";
import InfoModal from "./InfoModal";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Select } from "components/ui/select";

const HEALTH_CHECK_CLIENT_URL = Config.HEALTH_CHECK_CLIENT_URL;
const ENVIRONMENT_LIST = Config.ENVIRONMENT_LIST;
const STREAMCHECK_AUTH_URL = Config.STREAMCHECK_AUTH_URL;

function Filter({
    eventId,
    environment,
    duration,
    startTime,
    endTime,
    eventDetails,
    theme,
    toggleTheme,
    activeTabUrl,
}) {
    const [durationState, setDurationState] = useState(duration);
    const [showInfoModal, setShowInfoModal] = useState(false);

    function handleSelectTime(buttonValue) {
        setDurationState(buttonValue);
    }

    function filterEvent() {
        const baseURL = HEALTH_CHECK_CLIENT_URL;
        const eventId = document.getElementById("eventIdInput").value;
        const environment = document.getElementById("environmentIdInput").value;
        const startDateInput = document.getElementById("startDateInput");
        const endDateInput = document.getElementById("endDateInput");

        let queryParams = "";
        if (durationState !== "Range") {
            queryParams = `duration=PT${durationState}`;
        } else if (startDateInput && endDateInput) {
            const startTime = startDateInput.value;
            const endTime = endDateInput.value;
            queryParams = `startTime=${startTime}&endTime=${endTime}`;
        }

        const url = `${baseURL}?environment=${environment}&event_id=${eventId}&${queryParams}#${activeTabUrl}`;
        window.location.href = url;
    }

    function filterEventTimeRange() {
        const baseURL = HEALTH_CHECK_CLIENT_URL;
        const eventId = document.getElementById("eventIdInput").value;
        const environment = document.getElementById("environmentIdInput").value;

        let queryParams = `startTime=${eventDetails["streamStartDateTime"]}&endTime=${eventDetails["streamEndDateTime"]}#${activeTabUrl}`;
        const url = `${baseURL}?environment=${environment}&event_id=${eventId}&${queryParams}`;
        window.location.href = url;
    }

    function openConsoleUrl() {
        let consoleUrl =
            STREAMCHECK_AUTH_URL.split("login")[0] +
            "ui/beta/live-stream/schedule/event/" +
            eventId;
        window.open(consoleUrl, "_blank", "noreferrer");
    }

    return (
        <header className="border-b border-border bg-card/60 backdrop-blur">
            {getControlsTier()}
            {eventDetails && getEventContextTier()}
            <InfoModal
                showInfoModal={showInfoModal}
                setShowInfoModal={setShowInfoModal}
                theme={theme}
            />
        </header>
    );

    // Tier 1: filter inputs, with theme/info actions pushed to the right.
    function getControlsTier() {
        return (
            <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 sm:px-6">
                <div className="flex items-center gap-2">
                    <Input
                        type="text"
                        id="eventIdInput"
                        name="eventId"
                        defaultValue={eventId}
                        placeholder="Event ID"
                        title="Event ID"
                        className="w-[220px]"
                    />
                    <Select
                        id="environmentIdInput"
                        name="environment"
                        defaultValue={environment}
                        title="Environment"
                        className="w-auto"
                    >
                        {ENVIRONMENT_LIST.map((env) => (
                            <option key={env} value={env}>
                                {env}
                            </option>
                        ))}
                    </Select>
                </div>

                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-md border border-border p-0.5">
                        {["1H", "2H", "Range"].map((time) => (
                            <button
                                key={time}
                                type="button"
                                className={cn(
                                    "rounded px-3 py-1 text-sm font-medium transition-colors",
                                    time === durationState
                                        ? "bg-primary text-primary-foreground"
                                        : "text-muted-foreground hover:text-foreground"
                                )}
                                onClick={() => handleSelectTime(time)}
                            >
                                {time}
                            </button>
                        ))}
                    </div>
                    {durationState === "Range" && (
                        <div className="flex items-center gap-2">
                            <Input
                                type="datetime-local"
                                id="startDateInput"
                                name="startTime"
                                defaultValue={startTime}
                                title="Start Date"
                                className="w-auto"
                            />
                            <span className="text-muted-foreground">→</span>
                            <Input
                                type="datetime-local"
                                id="endDateInput"
                                name="endTime"
                                defaultValue={endTime}
                                title="End Date"
                                className="w-auto"
                            />
                        </div>
                    )}
                    <Button size="sm" onClick={filterEvent}>
                        <FilterIcon />
                        Filter
                    </Button>
                </div>

                <span className="flex-1" />

                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={toggleTheme}
                        title={theme === "light" ? "Dark Theme" : "Light Theme"}
                    >
                        {theme === "light" ? <Moon /> : <Sun />}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setShowInfoModal(true)}
                        title="Info"
                    >
                        <Info />
                    </Button>
                </div>
            </div>
        );
    }

    // Tier 2: condensed event context (description, status, channel, window) + actions.
    function getEventContextTier() {
        return (
            <div className="relative flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border bg-black/15 px-4 py-2 sm:px-6">
                <span className="text-sm font-semibold">
                    {eventDetails["description"]}
                    {eventDetails["watermarkingEvent"] && (
                        <span className="wm-label ms-2">(WM)</span>
                    )}
                </span>

                {getStatusPill()}

                <span className="text-border">|</span>
                <span className="flex items-center gap-1.5 whitespace-nowrap text-sm">
                    <span className="text-muted-foreground">Channel</span>
                    {eventDetails["channelId"]}
                </span>

                <span className="text-border">|</span>
                <span className="flex items-center gap-1.5 whitespace-nowrap text-sm">
                    <span className="text-muted-foreground">Window</span>
                    {eventDetails["streamStartDateTime"]} → {eventDetails["streamEndDateTime"]}
                </span>

                <span className="flex-1" />

                <Button variant="outline" size="sm" onClick={filterEventTimeRange}>
                    Filter Event time
                </Button>

                {getProgressBar()}
            </div>
        );
    }

    function getStatusPill() {
        const status = eventDetails["eventStatus"];
        const isRunning = status === "RUNNING";
        return (
            <span
                className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                    isRunning
                        ? "border-success/35 bg-success/15 text-success"
                        : "border-border bg-muted text-muted-foreground"
                )}
            >
                <span
                    className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        isRunning ? "bg-success" : "bg-muted-foreground"
                    )}
                />
                {status}
            </span>
        );
    }

    // Progress rendered as a 2px hairline pinned to the tier's bottom edge.
    function getProgressBar() {
        let progress = calculateProgress(
            eventDetails["streamStartDateTime"],
            eventDetails["streamEndDateTime"]
        );
        return (
            <div className="absolute inset-x-0 bottom-0 flex h-0.5 w-full overflow-hidden bg-muted">
                <div className="h-full bg-success" style={{ width: `${progress}%` }} />
            </div>
        );
    }

    function calculateProgress(startTimeString, endTimeString) {
        let startTime = new Date(startTimeString);
        let endTime = new Date(endTimeString);
        let now = new Date(getUTCFormattedTime());
        now = now <= startTime ? startTime : now;
        now = now >= endTime ? endTime : now;
        let progress = 100 - (Math.abs(endTime - now) * 100) / (endTime - startTime);
        return progress;
    }

    function getUTCFormattedTime() {
        const now = new Date();
        const year = now.getUTCFullYear();
        const month = String(now.getUTCMonth() + 1).padStart(2, "0");
        const day = String(now.getUTCDate()).padStart(2, "0");
        const hours = String(now.getUTCHours()).padStart(2, "0");
        const minutes = String(now.getUTCMinutes()).padStart(2, "0");
        const formattedTime = `${year}-${month}-${day}T${hours}:${minutes}`;
        return formattedTime;
    }
}

export default Filter;
